import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';

// import { ReactComponent as PaymentIcon } from '../../../assets/icons/amount-icon.svg';
import { ReactComponent as ClockIcon } from '../../../assets/icons/clock-icon.svg';
// import { ReactComponent as EnergyIcon } from '../../../assets/icons/energy-icon.svg';
// import SaveNature from '../../../assets/images/save-nature.png';
import { ReactComponent as InfoIcon } from '../../../assets/icons/info-icon.svg';
import { useCurrency } from '../../../config/branding';
import ToastMessage from '../../../shared-components/toast-message/ToastMessage';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
} from '../../../shared-functions/LocalStorage';
import charger from '../../../utils/api/charger';


import ChargingSpeedSocChart from './ChargingSpeedSocChart';
import StoreLink from './StoreLink';

interface SocKwApiPoint {
  ts: string;
  kw: number;
  soc: number;
}

/**
 * Payment method component
 * @param properties
 * @returns
 */
function Invoice() {
  const { t } = useTranslation('charger');
  const navigate = useNavigate();
  const currencyCode = useCurrency();
  const paidAmountRaw = getLocalStorageItem('powerUtilizationAmount');
  const paidAmount = Number(paidAmountRaw) || 0;
  const totalKwhConsumer = Number(getLocalStorageItem('totalKwhConsumer')) || 0;
  const idleFee = getLocalStorageItem('penaltyAmount') || 0;
  // const sessionFee = getLocalStorageItem('sessionFee') || 0;
  const platformFee = getLocalStorageItem('platformFee') || 0;
  const liveHours = Number(getLocalStorageItem('liveHours')) || 0;
  const totalAmount = Number(getLocalStorageItem('totalAmount')) || 0;
  const liveMinutes = Number(getLocalStorageItem('liveMinutes')) || 0;
  const [realTimeChargingData, setRealTimeChargingData] = useState<SocKwApiPoint[]>([]);
  const [transactionId, setTransactionId] = useState<string | null>(
    getLocalStorageItem('guestToken') || null,
  );
  const [chargerDetails, setChargerDetails] = useState<any>(null);
  const { deviceId } = useParams<{ deviceId: string }>(); // Extracts the ID from the URL
  const [toastStatus, setToastStatus] = useState({
    openToast: false,
    showToastMessage: '',
  });
  useEffect(() => {
    setTimeout(() => {
      removeLocalStorageItem('powerUtilizationAmount');
      removeLocalStorageItem('totalKwhConsumer');
      removeLocalStorageItem('liveCost');
      removeLocalStorageItem('liveHours');
      removeLocalStorageItem('liveMinutes');
      removeLocalStorageItem('paidAmount');
      removeLocalStorageItem('idleState');
      removeLocalStorageItem('penaltyAmount');
      removeLocalStorageItem('sessionFee');
      removeLocalStorageItem('platformFee');
      removeLocalStorageItem('totalAmount');
      removeLocalStorageItem('selectedConnector');
      removeLocalStorageItem('guestToken');
      removeLocalStorageItem('chargingStatus');
      removeLocalStorageItem('domain');
    }, 5000);
  }, []);

  /**
   * Populate edit form when entering edit mode
   */
  const handleActiveSession = async () => {
    try {
      const response = await charger.refreshSession({ transactionId, chargingPointSn: deviceId });
      const session = response?.data || null;
      if (session?.socVsKW?.length) {
        const history: SocKwApiPoint[] = session.socVsKW.map((p: any) => ({
          ts: p.ts,
          kw: p.kw ?? p.kW ?? 0,
          soc: p.soc,
        }));
        setRealTimeChargingData(history);
      }
    } catch (error: any) {
      console.error(t('selectConnector'));
    }
  };

  useEffect(() => {
    if (deviceId && transactionId) {
      setTransactionId(transactionId);
      handleActiveSession();
    }
  }, [deviceId, transactionId]);

  /**
   * Get charger details
   */
  const getChargerDetails = async () => {
    try {
      const response = await charger.fetchOneDevicePoints(deviceId ?? '');
      const apiResponse = response.data;
      setChargerDetails(apiResponse);
    } catch (error: any) {
      console.error('Failed to fetch charger details:', error);
    }
  };

  useEffect(() => {
    if (deviceId) {
      getChargerDetails();
    }
  }, [deviceId]);

  return (
    <div className="invoice-wrap">
      <ToastMessage
        open={toastStatus.openToast}
        onClose={() =>
          setToastStatus({
            openToast: false,
            showToastMessage: '',
          })
        }
        type="error"
        message={toastStatus.showToastMessage || t('somethingWentWrong')}
        onCloseTimer={6000}
      />
      <div className="invoice-container session-overview">
        <div className="session-header">
          <div className="session-title">
            <h2>{t('sessionsOverview', { defaultValue: 'Sessions Overview' })}</h2>
            <p className="station-id">Station ID : {chargerDetails?.chargingStation?.name || '-'}</p>
          </div>
          <div className="session-time">
            <ClockIcon className="icon" />
            <span>{liveHours || 0}hr {liveMinutes || 0}min</span>
          </div>
        </div>

        <div className="session-details-list">
          <div className="detail-row">
            <span className="label">{t('powerConsumed', { defaultValue: 'Power Consumed' })}</span>
            <span className="separator">-</span>
            <span className="value">{(totalKwhConsumer || 0).toFixed(3)} kW</span>
          </div>
          <div className="detail-row">
            <span className="label">{t('chargingFee', { defaultValue: 'Charging Fee' })}</span>
            <span className="separator">-</span>
            <span className="value flex-value">{(paidAmount || 0).toFixed(2)} {currencyCode}</span>
          </div>
          {/* <div className="detail-row">
            <span className="label">{t('sessionFee', { defaultValue: 'Session Fee' })}</span>
            <span className="separator">-</span>
            <span className="value flex-value">{Number(sessionFee).toFixed(2)} </span>
          </div> */}
          <div className="detail-row">
            <span className="label">{t('platformFee', { defaultValue: 'Platform Fee' })}</span>
            <span className="separator">-</span>
            <span className="value flex-value">{Number(platformFee).toFixed(2)} {currencyCode}</span>
          </div>
          <div className="detail-row">
            <span className="label">{t('idleFee', { defaultValue: 'Idle Fee' })}</span>
            <span className="separator">-</span>
            <span className="value flex-value">{Number(idleFee).toFixed(2)} {currencyCode}</span>
          </div>
          <div className="detail-row">
            <span className="label">{t('totalAmount', { defaultValue: 'Total Amount' })}</span>
            <span className="separator">-</span>
            <span className="value flex-value">{(totalAmount || 0).toFixed(2)} {currencyCode}</span>
          </div>
        </div>

        <div className="session-green-message">
          <InfoIcon className="info-icon" />
          <p>
            {t('For AED customers, amounts are converted at the rate of')}{' '}
            <strong>1.00 AED</strong> = <strong>1.02 SAR</strong>{' '}
            {t('temporarily')}
          </p>
        </div>

        <button type="button" className="go-home-btn" onClick={() => navigate(`/charger?deviceId=${deviceId}`)}>{t('goToHome', { defaultValue: 'Go to Home' })}</button>
      </div>
      <div className="charging-speed-soc-graph">
        <ChargingSpeedSocChart data={realTimeChargingData} available />
      </div>
      <StoreLink />
    </div>
  );
}

export default Invoice;
